import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import "./Home3.css";
import HomeCarousel from "./HomeCarousel";
import IntroStory from "./IntroStory";

const Home3 = () => {
  const navigate = useNavigate();
  const heroRef = useRef(null);

  const [showIntro, setShowIntro] = useState(false);

  const dismissIntro = (nextPath) => {
    setShowIntro(false);
    if (nextPath) navigate(nextPath);
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("in-view");
          }
        });
      },
      { threshold: 0.15 }
    );

    document.querySelectorAll(".h3-reveal").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="h3-home">
      {showIntro && <IntroStory onClose={dismissIntro} />}
      <Helmet>
        <title>dmyor | Your personal intent archive</title>
        <meta name="description" content="Your personal intent archive. Write what you're going to build before you build it. Document every step. Keep a permanent record of how far you got." />
        <link rel="canonical" href="https://www.dmyor.com/" />
        <meta property="og:title" content="dmyor | Your personal intent archive" />
        <meta property="og:description" content="Your personal intent archive. Write what you're going to build before you build it. Document every step. Keep a permanent record of how far you got." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.dmyor.com/" />
        <meta property="og:image" content="https://dmyor.com/images/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="dmyor | Your personal intent archive" />
        <meta name="twitter:description" content="Your personal intent archive. Write what you're going to build before you build it. Document every step. Keep a permanent record of how far you got." />
        <meta name="twitter:image" content="https://dmyor.com/images/og-image.png" />
      </Helmet>

      {/* ── STORY TRIGGER ── */}
      <div className="h3-story-wrap">
        <button className="h3-story-btn" onClick={() => setShowIntro(true)}>
          <span className="h3-story-dot" />
          Understand dmyor in 5 slides
          <span className="h3-story-arrow">→</span>
        </button>
      </div>

      {/* ── HERO ── */}
      <section className="h3-hero" ref={heroRef}>
        <div className="h3-hero-inner">
          <div className="h3-hero-label h3-reveal">Reality Construction Platform</div>

          <h1 className="h3-hero-title h3-reveal">
            Write the reality<br />
            that doesn't<br />
            <span className="h3-accent">exist yet.</span>
          </h1>

          <p className="h3-hero-sub h3-reveal">
            Everyone has something they're going to do. Few write it down. Fewer still keep a record of how they got there.
          </p>

          <div className="h3-hero-actions h3-reveal">
            <Link to="/start" className="h3-btn-primary">
              Write your reality
            </Link>
            <Link to="/about" className="h3-btn-ghost">
              Our story
            </Link>
          </div>

          <div className="h3-hero-footnote h3-reveal">
            Anonymous until you build it.
          </div>
          <div className="h3-hero-ai-badge h3-reveal">
            <span className="h3-hero-ai-dot" />
            AI-powered planning inside every record
          </div>
        </div>

        <div className="h3-hero-visual" aria-hidden="true">
          <div className="h3-grid-bg"></div>
          <div className="h3-node h3-node-1">
            <span className="h3-node-status">Active</span>
            <span className="h3-node-text">Buy my first place before 30.</span>
            <span className="h3-node-days">847 days remaining</span>
          </div>
          <div className="h3-node h3-node-2">
            <span className="h3-node-status">Active</span>
            <span className="h3-node-text">Lose 20kg in one year. No excuses.</span>
            <span className="h3-node-days">312 days remaining</span>
          </div>
          <div className="h3-node h3-node-3">
            <span className="h3-node-status">Active</span>
            <span className="h3-node-text">Scale my instagram to 100K</span>
            <span className="h3-node-days">214 days remaining</span>
          </div>
        </div>
      </section>

      {/* ── INTENT ARCHIVE ── */}
      <section className="h3-ia">
        <div className="h3-ia-inner">

          <div className="h3-ia-header h3-reveal">
            <div className="h3-eyebrow">Intent Archive</div>
            <h2 className="h3-ia-heading">
              The world's <span className="h3-ia-accent">intent archive.</span>
            </h2>
            <p className="h3-ia-body">
              A record of intentions, dreams, and journeys — written by people who decided to start. Kept for the future to read.
            </p>
          </div>

          <div className="h3-log h3-reveal">
            <div className="h3-log-head">
              <span>Year</span>
              <span>Author</span>
              <span>Vision</span>
              <span>Status</span>
            </div>
            <div className="h3-log-row">
              <span className="h3-log-year">2028</span>
              <span className="h3-log-author">S.P.</span>
              <p className="h3-log-text">"I'm starting my own business this year. By year 5, it's paying for everything I want to do."</p>
              <span className="h3-status h3-status-released">Released</span>
            </div>
            <div className="h3-log-row">
              <span className="h3-log-year">2027</span>
              <span className="h3-log-author">M.R.</span>
              <p className="h3-log-text">"I'm building my Instagram page on AI tools. 100k followers in 3 years, brand deals by year 2."</p>
              <span className="h3-status h3-status-released">Released</span>
            </div>
            <div className="h3-log-row">
              <span className="h3-log-year">2026</span>
              <span className="h3-log-author">D.K.</span>
              <p className="h3-log-text">"I'm building enough investments to retire by 40. Five years of serious saving and compounding starts now."</p>
              <span className="h3-status h3-status-released">Released</span>
            </div>
            <div className="h3-log-row h3-log-row-you">
              <span className="h3-log-year h3-log-year-you">2026</span>
              <span className="h3-log-author">You</span>
              <p className="h3-log-text h3-log-text-pending"><span className="h3-cursor">|</span></p>
              <span className="h3-status h3-status-awaiting">Awaiting</span>
            </div>
          </div>

          <div className="h3-ia-cta-row h3-reveal">
            <Link to="/start" className="h3-ia-cta">
              <span>Write your chapter</span>
            </Link>
          </div>

        </div>
      </section>

      {/* ── WE HELP YOU FIGURE IT OUT ── */}
      <section className="h3-fig">
        <div className="h3-fig-inner">

          <div className="h3-fig-header h3-reveal">
            <div className="h3-eyebrow">Figure it out</div>
            <h2 className="h3-fig-heading">
              We saw what you dream of doing. <span className="h3-fig-accent">Now we help you make it real.</span>
            </h2>
            <p className="h3-fig-body">
              You don't need to know the path. Dmyor Assist walks it with you.
            </p>
          </div>

          <div className="h3-fig-example h3-reveal">
            <span className="h3-fig-example-eyebrow">Following one vision</span>
            <p className="h3-fig-example-vision">"I want to start the business I've been thinking about for years and finally work for myself."</p>
          </div>

          <div className="h3-fig-flow">

            {/* Step 1 */}
            <div className="h3-fig-step h3-reveal">
              <div className="h3-fig-grid">
                <div className="h3-fig-text">
                  <div className="h3-fig-meta">
                    <span className="h3-fig-num">01</span>
                    <span className="h3-fig-tag">Records</span>
                  </div>
                  <h3 className="h3-fig-title">Dmyor Assist asks the right questions, and helps you shape your records.</h3>
                  <p className="h3-fig-desc">
                    Records are what you're doing to make your vision real. Dmyor Assist asks the right questions and helps you shape them.
                  </p>
                </div>
                <div className="h3-fig-card">
                  <div className="h3-card-head">
                    <span className="h3-card-eyebrow">Dmyor Assist</span>
                    <span className="h3-card-badge">Intake</span>
                  </div>
                  <div className="h3-chat h3-chat-ai">
                    <p>What's the business actually about? And do you have a customer in mind?</p>
                  </div>
                  <div className="h3-chat h3-chat-you">
                    <p>A coaching service for new managers. People who got promoted but feel lost.</p>
                  </div>
                  <div className="h3-chat h3-chat-ai">
                    <p>Clear. Here are three records to start with:</p>
                  </div>
                  <div className="h3-records">
                    <div className="h3-record">
                      <span className="h3-record-dot" style={{ background: "#34d399" }} />
                      <span>Talking to 20 new managers to learn what they're really struggling with.</span>
                    </div>
                    <div className="h3-record">
                      <span className="h3-record-dot" style={{ background: "#ff8a2a" }} />
                      <span>Building a small audience by sharing what I learn online.</span>
                    </div>
                    <div className="h3-record">
                      <span className="h3-record-dot" style={{ background: "#a78bfa" }} />
                      <span>Shaping my first paid offer and getting it in front of real people.</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 2 */}
            <div className="h3-fig-step h3-reveal">
              <div className="h3-fig-grid h3-fig-grid-reverse">
                <div className="h3-fig-card">
                  <div className="h3-card-head">
                    <span className="h3-card-eyebrow">Dmyor Assist</span>
                    <span className="h3-card-badge h3-card-badge-violet">This week</span>
                  </div>
                  <div className="h3-todo-list">
                    <div className="h3-todo-item">
                      <span className="h3-todo-check" />
                      <span className="h3-todo-text">Reach out to 5 new managers in your network for a 20-min call</span>
                      <span className="h3-todo-pill h3-todo-pill-blue">Focus</span>
                    </div>
                    <div className="h3-todo-item">
                      <span className="h3-todo-check" />
                      <span className="h3-todo-text">Write one post about the most common struggle you've heard so far</span>
                      <span className="h3-todo-pill h3-todo-pill-green">Quick</span>
                    </div>
                    <div className="h3-todo-item">
                      <span className="h3-todo-check" />
                      <span className="h3-todo-text">Draft the rough outline of your first paid offer by Friday</span>
                      <span className="h3-todo-pill h3-todo-pill-orange">Push</span>
                    </div>
                  </div>
                </div>
                <div className="h3-fig-text">
                  <div className="h3-fig-meta">
                    <span className="h3-fig-num">02</span>
                    <span className="h3-fig-tag">Todos</span>
                  </div>
                  <h3 className="h3-fig-title">Then it turns those records into a clear week of work.</h3>
                  <p className="h3-fig-desc">
                    Dmyor Assist turns your records into specific weekly todos. No more guessing what to do.
                  </p>
                </div>
              </div>
            </div>

            {/* Step 3 */}
            <div className="h3-fig-step h3-reveal">
              <div className="h3-fig-grid">
                <div className="h3-fig-text">
                  <div className="h3-fig-meta">
                    <span className="h3-fig-num">03</span>
                    <span className="h3-fig-tag">Journey</span>
                  </div>
                  <h3 className="h3-fig-title">Every step becomes a documented journey.</h3>
                  <p className="h3-fig-desc">
                    Every update, every win, every lesson is saved. A year later, it's all there.
                  </p>
                </div>
                <div className="h3-fig-card">
                  <div className="h3-card-head">
                    <span className="h3-card-eyebrow">Your Journey</span>
                    <span className="h3-card-badge h3-card-badge-orange">Live</span>
                  </div>
                  <div className="h3-tl">
                    <div className="h3-tl-item">
                      <span className="h3-tl-dot" />
                      <div className="h3-tl-content">
                        <span className="h3-tl-date">Feb 12</span>
                        <p>Did the first 5 calls. Same pattern in every conversation: nobody taught them how to lead.</p>
                      </div>
                    </div>
                    <div className="h3-tl-item">
                      <span className="h3-tl-dot" />
                      <div className="h3-tl-content">
                        <span className="h3-tl-date">Mar 04</span>
                        <p>First post hit 400 likes. Three people DM'd asking if I work with managers 1-on-1.</p>
                      </div>
                    </div>
                    <div className="h3-tl-item">
                      <span className="h3-tl-dot h3-tl-dot-active" />
                      <div className="h3-tl-content">
                        <span className="h3-tl-date">Apr 19</span>
                        <p>First paying client signed. Six week program. This is actually a thing now.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>

          <p className="h3-fig-universal h3-reveal">This works for anything you're trying to build, become, or finish.</p>

          <div className="h3-fig-cta-row h3-reveal">
            <Link to="/start" className="h3-fig-cta">
              <span>Start with Dmyor Assist</span>
            </Link>
          </div>

        </div>
      </section>

      {/* ── VISION LIFECYCLE ── */}
      <section className="h3-vlc">
        <div className="h3-vlc-inner">

          <div className="h3-vlc-header h3-reveal">
            <h2 className="h3-vlc-heading">
              Vision <span className="h3-vlc-accent">Lifecycle.</span>
            </h2>
          </div>

          <div className="h3-vlc-circles h3-reveal">

            <div className="h3-vlc-node">
              <div className="h3-vlc-circle">
                <span className="h3-vlc-num">01</span>
              </div>
              <h3 className="h3-vlc-title">You write the vision.</h3>
              <p className="h3-vlc-desc">Start your journey.</p>
            </div>

            <span className="h3-vlc-arrow" aria-hidden="true">→</span>

            <div className="h3-vlc-node">
              <div className="h3-vlc-circle">
                <span className="h3-vlc-num">02</span>
              </div>
              <h3 className="h3-vlc-title">You make it real.</h3>
              <p className="h3-vlc-desc">Document every struggle, every small win.</p>
            </div>

            <span className="h3-vlc-arrow" aria-hidden="true">→</span>

            <div className="h3-vlc-node">
              <div className="h3-vlc-circle h3-vlc-circle-final">
                <span className="h3-vlc-num">03</span>
              </div>
              <h3 className="h3-vlc-title">You release it.</h3>
              <p className="h3-vlc-desc">It now has your name on it, permanently. Everyone can read your journey now!</p>
            </div>

          </div>

        </div>
      </section>

      {/* ── HONEST WITH YOURSELF ── */}
      <section className="h3-dr">
        <div className="h3-dr-inner">

          <div className="h3-dr-left h3-reveal">
            <div className="h3-dr-note">I want to build something.</div>
            <div className="h3-dr-note">In 1 year my life should be different.</div>
            <div className="h3-dr-note">I know what I want. I just don't know where to start.</div>
            <div className="h3-dr-note">I'm going to make this thing happen.</div>
          </div>

          <div className="h3-dr-right h3-reveal">
            <h2 className="h3-dr-heading">
              Don't forget why<br />you started.
            </h2>
            <p className="h3-dr-body">
              Ever had these thoughts? Write down what you want and document the process as you go. Most people underestimate how much just putting it into words can clear things up.
            </p>
            <p className="h3-dr-body">
              Most people forget why they started. The frustration, the spark, the moment something clicked. Keep your roots — your future self will need to read them back.
            </p>
            <Link to="/start" className="h3-dr-cta">Start your record</Link>
          </div>

        </div>
      </section>

      {/* ── PROJECT-5 STRIP ── */}
      <section className="h3-p5">
        <div className="h3-p5-inner h3-reveal">
          <div className="h3-p5-left">
            <span className="h3-p5-title">PROJECT-5</span>
            <span className="h3-p5-sub">A 5-year commitment to designing the life you want. Write it. Build it. Release it when it's real.</span>
          </div>
          <Link to="/project-5" className="h3-p5-link" aria-label="Learn about Project 5">
            ?
          </Link>
        </div>
      </section>

      <div className="h3-carousel-shell">
        <HomeCarousel />
      </div>

      {/* ── ANONYMOUS UNTIL RELEASED ── */}
      <section className="h3-anon">
        <div className="h3-anon-inner">
          <div className="h3-anon-header h3-reveal">
            <div className="h3-eyebrow">Privacy</div>
            <h2 className="h3-anon-heading">
              <span className="h3-anon-accent">Anonymous</span> by default.
            </h2>
            <p className="h3-anon-body">
              Anyone can read what you wrote. No one can see who wrote it. When you've made it real, release the record to attach your name.
            </p>
          </div>

          <div className="h3-anon-cards h3-reveal">
            <div className="h3-anon-card">
              <span className="h3-anon-status">Anonymous</span>
              <p className="h3-anon-text">"I will finally start the business I've been thinking about for 3 years."</p>
              <div className="h3-anon-author">
                <span className="h3-anon-author-label">Author</span>
                <span className="h3-anon-redacted">REDACTED</span>
              </div>
              <p className="h3-anon-note">The default. Your record is public. You aren't.</p>
            </div>

            <div className="h3-anon-card">
              <span className="h3-anon-status h3-anon-status-private">Hidden</span>
              <p className="h3-anon-text">"I'm going to get my finances in order before I turn 30."</p>
              <div className="h3-anon-author">
                <span className="h3-anon-author-label">Author</span>
                <span className="h3-anon-private-name">Only you</span>
              </div>
              <p className="h3-anon-note">Mark it private. It leaves the archive entirely.</p>
            </div>

            <div className="h3-anon-card">
              <span className="h3-anon-status h3-anon-status-released">Released</span>
              <p className="h3-anon-text">"I stopped waiting and shipped. Here's how it happened."</p>
              <div className="h3-anon-author">
                <span className="h3-anon-author-label">Author</span>
                <span className="h3-anon-author-real">Aman</span>
              </div>
              <p className="h3-anon-note">When you've made it real, release it. Your name appears.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── WHY DOCUMENT ── */}
      <section className="h3-wd">
        <div className="h3-wd-inner">
          <div className="h3-wd-header h3-reveal">
            <div className="h3-eyebrow">Why document</div>
            <h2>Because the record matters.</h2>
          </div>

          <div className="h3-wd-grid">
            <div className="h3-wd-item h3-reveal">
              <span className="h3-wd-num">01</span>
              <div>
                <h4>Your future self will read this.</h4>
                <p>Before the outcome, before the proof — there was a version of you who just decided. That moment deserves to be kept.</p>
              </div>
            </div>

            <div className="h3-wd-item h3-reveal">
              <span className="h3-wd-num">02</span>
              <div>
                <h4>The world deserves to know these thoughts existed.</h4>
                <p>Someone will read your story one day and choose to start. Your intention matters beyond you.</p>
              </div>
            </div>

            <div className="h3-wd-item h3-reveal">
              <span className="h3-wd-num">03</span>
              <div>
                <h4>Most intentions never leave the mind.</h4>
                <p>Writing yours down is the first act of making it real. Everything else follows from that.</p>
              </div>
            </div>

            <div className="h3-wd-item h3-reveal">
              <span className="h3-wd-num">04</span>
              <div>
                <h4>You cannot see your own growth without a record.</h4>
                <p>Distance only becomes visible when you can look back at where you started.</p>
              </div>
            </div>

            <div className="h3-wd-item h3-reveal">
              <span className="h3-wd-num">05</span>
              <div>
                <h4>Memory rewrites the past. Writing doesn't.</h4>
                <p>Your updates are timestamped and permanent — the real story of how it happened, not the edited version you'll remember later.</p>
              </div>
            </div>

            <div className="h3-wd-item h3-reveal">
              <span className="h3-wd-num">06</span>
              <div>
                <h4>Written intentions are harder to abandon.</h4>
                <p>Commitment that lives only in your head is easy to walk away from. A written record holds you to what you said you would do.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── RECORD LIFECYCLE ── */}
      <section className="h3-rl">
        <div className="h3-rl-inner">
          <div className="h3-rl-header h3-reveal">
            <div className="h3-eyebrow">Lifecycle</div>
            <h2 className="h3-rl-heading">From <span className="h3-rl-accent">first word</span> to <span className="h3-rl-accent">final release</span>.</h2>
            <p className="h3-rl-body">A record isn't a one-time post. It lives, grows, and ends on your terms.</p>
          </div>

          <div className="h3-rl-timeline">
            <div className="h3-rl-line" aria-hidden="true"></div>

            <div className="h3-rl-stage h3-rl-stage-left h3-reveal">
              <div className="h3-rl-node">
                <span className="h3-rl-num">01</span>
              </div>
              <div className="h3-rl-content">
                <span className="h3-rl-tag">Create</span>
                <h3 className="h3-rl-title">Set the record. Set the duration.</h3>
                <p className="h3-rl-desc">Write the one thing you're going to do. Pick how long you're giving yourself: 1 year, 3 years, 5 years. The clock starts ticking the moment you commit.</p>
              </div>
            </div>

            <div className="h3-rl-stage h3-rl-stage-right h3-reveal">
              <div className="h3-rl-node">
                <span className="h3-rl-num">02</span>
              </div>
              <div className="h3-rl-content">
                <span className="h3-rl-tag">Build</span>
                <h3 className="h3-rl-title">Update as you go. AI Assist when you're stuck.</h3>
                <p className="h3-rl-desc">Log progress, breakthroughs, setbacks. Talk to AI Assist to think through next steps, plan side quests, or pressure-test your strategy. The record grows with you.</p>
              </div>
            </div>

            <div className="h3-rl-stage h3-rl-stage-left h3-reveal">
              <div className="h3-rl-node">
                <span className="h3-rl-num">03</span>
              </div>
              <div className="h3-rl-content">
                <span className="h3-rl-tag">Release</span>
                <h3 className="h3-rl-title">When it's real, release it.</h3>
                <p className="h3-rl-desc">The record becomes public under your name, with the entire build log attached. Permanent proof you said you'd do it, then did it.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── DMYOR AI ── */}
      <section className="h3-dma">
        <div className="h3-dma-inner">

          <div className="h3-dma-left h3-reveal">
            <div className="h3-eyebrow">Dmyor Assist</div>
            <h2>Need someone to think this through with?</h2>
            <p className="h3-dma-sub">
              Your AI reads everything you've written — your record, your updates,
              your timeline. It meets you where you actually are.
            </p>

            <div className="h3-dma-modes">
              {[
                { color: "#34d399", label: "Discuss",    desc: "Just talk through where you are." },
                { color: "#a78bfa", label: "Challenge",  desc: "Get pushed on what you're avoiding." },
                { color: "#22d3ee", label: "Mirror",     desc: "Hear your own thinking reflected back." },
                { color: "#f472b6", label: "Positive",   desc: "Perspective and grounded encouragement." },
                { color: "#ff8a2a", label: "Strategist", desc: "Specific next steps based on your reality." },
              ].map((m) => (
                <div className="h3-dma-mode" key={m.label}>
                  <span className="h3-dma-mode-dot" style={{ background: m.color }} />
                  <div className="h3-dma-mode-text">
                    <span className="h3-dma-mode-label">{m.label}</span>
                    <span className="h3-dma-mode-desc">{m.desc}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="h3-dma-right h3-reveal">
            <div className="h3-dma-mockup">
              <div className="h3-dma-mockup-header">
                <span className="h3-dma-mockup-title">Dmyor Assist</span>
                <span className="h3-dma-mockup-badge">Strategist</span>
              </div>

              <div className="h3-dma-mockup-node">
                <span className="h3-dma-mockup-node-label">Record</span>
                <span className="h3-dma-mockup-node-text">100K followers on Instagram, brand deals, and a product of my own.</span>
              </div>

              <div className="h3-dma-mockup-chat">
                <div className="h3-dma-bubble h3-dma-bubble-user">
                  I haven't posted anything in two weeks. Feel like I'm falling behind.
                </div>

                <div className="h3-dma-bubble h3-dma-bubble-ai">
                  <span className="h3-dma-bubble-mode">Strategist</span>
                  <p>You've logged 14 updates since you started — you're not falling behind, you're between sprints.</p>
                  <div className="h3-dma-bubble-steps">
                    <div className="h3-dma-bubble-step"><span className="h3-dma-step-num">1</span><span>Post one piece of content this week. Anything. Ship it.</span></div>
                    <div className="h3-dma-bubble-step"><span className="h3-dma-step-num">2</span><span>Review what performed best in your last batch.</span></div>
                    <div className="h3-dma-bubble-step"><span className="h3-dma-step-num">3</span><span>Block 2 hours this weekend to batch-create ahead.</span></div>
                  </div>
                </div>
              </div>

              <div className="h3-dma-mockup-switcher">
                <span className="h3-dma-switcher-label">Switch mode</span>
                <div className="h3-dma-switcher-pills">
                  {[
                    { label: "Discuss",    color: "#34d399" },
                    { label: "Challenge",  color: "#a78bfa" },
                    { label: "Mirror",     color: "#22d3ee" },
                    { label: "Positive",   color: "#f472b6" },
                    { label: "Strategist", color: "#ff8a2a" },
                  ].map((m) => (
                    <span
                      key={m.label}
                      className={`h3-dma-pill ${m.label === "Strategist" ? "h3-dma-pill-active" : ""}`}
                      style={m.label === "Strategist" ? { borderColor: m.color, color: m.color, background: `${m.color}1f` } : {}}
                    >
                      {m.label}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* ── PROOF STRIP ── */}
      <section className="h3-tr">
        <div className="h3-tr-inner h3-reveal">
          <div className="h3-tr-card h3-tr-then">
            <span className="h3-tr-label">Written</span>
            <p className="h3-tr-text">"I am starting a YouTube channel. 100k subscribers in 2 years. I have zero videos right now."</p>
            <span className="h3-tr-meta">Committed. Anonymous. Day one.</span>
          </div>

          <div className="h3-tr-arrow" aria-hidden="true">
            <div className="h3-tr-arrow-line"></div>
            <span className="h3-tr-arrow-gap">2 years later</span>
            <div className="h3-tr-arrow-line"></div>
          </div>

          <div className="h3-tr-card h3-tr-now">
            <span className="h3-tr-label h3-tr-label-released">Released</span>
            <p className="h3-tr-text">"112k subscribers. 87 videos. The channel is real. I wrote this before I even bought a mic."</p>
            <span className="h3-tr-meta">V.S., released after 23 months</span>
          </div>
        </div>
      </section>

      {/* ── ANONYMOUS ── */}
      <section className="h3-anon2">
        <div className="h3-anon2-inner h3-reveal">
          <h2>
            Anonymous until<br />
            you earn the name.
          </h2>
          <div className="h3-anon2-body">
            <p>
              Everything written on dmyor is anonymous by default. No
              audience. No performance. No one watching you fail or pivot or
              go quiet for three months.
            </p>
            <p>
              The only moment your name appears is when you release. When the
              reality you wrote has become real. That is the only credential
              that matters here.
            </p>
            <Link to="/s" className="h3-link-orange">
              See what people are building →
            </Link>
          </div>
        </div>
      </section>

      {/* ── FINAL CTA ── */}
      <section className="h3-cta">
        <div className="h3-cta-inner h3-reveal">
          <p className="h3-cta-pre">The only question is:</p>
          <h2 className="h3-cta-title">
            Are you ready to design your<br />
            <span className="h3-accent">reality?</span>
          </h2>
          <p className="h3-cta-body">
            Write the reality that doesn't exist yet. Set the deadline.
            Start building. Release it when it's real.
          </p>
          <Link to="/login" className="h3-btn-primary h3-btn-large">
            Write your first reality
          </Link>
          <p className="h3-cta-note">Anonymous. Yours.</p>
        </div>
      </section>
    </div>
  );
};

export default Home3;
